/**
 * "Chaves de IA" section rendered inside `/configuracoes`.
 *
 * Shows the OpenAI BYOK card in current OpenAI-only mode. Legacy
 * Anthropic/Google card definitions remain available for an intentional
 * `VITE_AI_PROVIDER_MODE=multi` rollback.
 *
 * Spec: docs/architecture/00-planning/02-byok-and-billing.md §"UX flows"
 * Refactored: Sprint 1.1 follow-up (UX feedback — stacked, no tabs).
 */

import { useState } from "react";
import {
  ArrowSquareOut,
  Brain,
  CheckCircle,
  CurrencyDollar,
  GoogleLogo,
  Key,
  OpenAiLogo,
  Question,
  Warning,
  WarningCircle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { LoadingState, ErrorState } from "@/components/states";
import { useByokKeys, useDeleteByokKey, useTestByokKey } from "../hooks";
import { useSyncByokToken } from "../hooks/useSyncByokToken";
import { AddKeyForm } from "./AddKeyForm";
import { StatusBadge } from "./StatusBadge";
import type { ByokKey, ProviderId } from "../types";
import { OPENAI_ONLY_MODE } from "@/lib/ai/providerPolicy";

interface ProviderConfig {
  id: ProviderId;
  label: string;
  tagline: string;
  placeholder: string;
  Icon: typeof Brain;
  required: boolean;
  link: string;
  /** Direct link to the provider's billing/credit page (where to add funds). */
  billingLink: string;
  howToSteps: string[];
}

// Legacy provider definitions stay in code for reversible multi-provider mode.
const PROVIDERS: ProviderConfig[] = [
  {
    id: "openai",
    label: "OpenAI",
    tagline:
      "Obrigatória. Transcrição, análise e geração usam sua chave OpenAI.",
    placeholder: "sk-...",
    Icon: OpenAiLogo,
    required: true,
    link: "https://platform.openai.com/api-keys",
    billingLink: "https://platform.openai.com/settings/organization/billing/overview",
    howToSteps: [
      "Acesse platform.openai.com e crie uma conta.",
      "Adicione crédito (mín. US$ 5) em Billing.",
      "Em API Keys → Create new secret key. Copie (sk-...).",
    ],
  },
  {
    id: "anthropic",
    label: "Anthropic",
    tagline:
      "Recomendada. Produz a melhor copy para posicionamento e estratégia.",
    placeholder: "sk-ant-...",
    Icon: Brain,
    required: false,
    link: "https://console.anthropic.com/settings/keys",
    billingLink: "https://console.anthropic.com/settings/billing",
    howToSteps: [
      "Acesse console.anthropic.com e crie uma conta.",
      "Vá em Settings → Billing e adicione crédito (mín. US$ 5).",
      "Em API Keys → Create Key. Copie a chave (sk-ant-...).",
    ],
  },
  {
    id: "google",
    label: "Google (Gemini)",
    tagline:
      "Recomendada. Entra como fallback robusto e mantém o sistema vivo se outros caírem.",
    placeholder: "Cole sua chave (AIza… ou AQ…)",
    Icon: GoogleLogo,
    required: false,
    link: "https://aistudio.google.com/app/apikey",
    billingLink: "https://console.cloud.google.com/billing",
    howToSteps: [
      "Acesse aistudio.google.com com sua conta Google.",
      "Em Get API Key → Create API key.",
      "Copie a chave e cole abaixo.",
    ],
  },
];

const VISIBLE_PROVIDERS = OPENAI_ONLY_MODE
  ? PROVIDERS.filter((provider) => provider.id === "openai")
  : PROVIDERS;

function isUsable(k: ByokKey): boolean {
  return k.status === "valid" || k.status === "pending" || k.status === "rate_limited";
}

function InlineStatusAlert({ keyRow }: { keyRow: ByokKey }) {
  if (keyRow.status === "invalid_auth") {
    return (
      <div
        className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-red-400"
        role="alert"
      >
        <Warning weight="bold" className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>Chave inválida. Verifique se copiou corretamente e cadastre novamente.</span>
      </div>
    );
  }
  if (keyRow.status === "quota_exhausted") {
    return (
      <div
        className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-yellow-400"
        role="alert"
      >
        <Warning weight="bold" className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="flex-1">Cota da API esgotada. Considere fazer upgrade no painel do provedor.</span>
      </div>
    );
  }
  return null;
}

function DeleteConfirmRow({
  onCancel,
  onConfirm,
  isPending,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  isPending: boolean;
}) {
  return (
    <div className="flex items-center gap-2 rounded-lg bg-destructive/15 border border-destructive/30 px-3 py-2">
      <span className="text-sm text-destructive flex-1">Tem certeza?</span>
      <Button variant="ghost" size="sm" onClick={onCancel} disabled={isPending}>
        Cancelar
      </Button>
      <Button variant="destructive" size="sm" onClick={onConfirm} disabled={isPending}>
        {isPending ? "Removendo..." : "Excluir"}
      </Button>
    </div>
  );
}

function KeyRow({ keyRow }: { keyRow: ByokKey }) {
  const del = useDeleteByokKey();
  const test = useTestByokKey();
  const [confirming, setConfirming] = useState(false);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border/50 bg-card p-4">
        <div className="flex-1 min-w-[12rem]">
          <p className="text-sm text-muted-foreground">Final ••••{keyRow.last4}</p>
          {keyRow.last_tested_at && (
            <p className="text-xs text-muted-foreground/70">
              Testada em {new Date(keyRow.last_tested_at).toLocaleString("pt-BR")}
            </p>
          )}
          {keyRow.last_error_code && keyRow.status !== "valid" && (
            <p className="text-xs text-red-400">Erro: {keyRow.last_error_code}</p>
          )}
        </div>
        <StatusBadge status={keyRow.status} />
        <Button
          variant="outline"
          size="sm"
          onClick={() => test.mutate(keyRow.id)}
          disabled={test.isPending}
        >
          {test.isPending ? "Testando..." : "Testar agora"}
        </Button>
        {!confirming && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfirming(true)}
            disabled={del.isPending}
          >
            Remover
          </Button>
        )}
      </div>
      {confirming && (
        <DeleteConfirmRow
          onCancel={() => setConfirming(false)}
          onConfirm={() => del.mutate(keyRow.id, { onSettled: () => setConfirming(false) })}
          isPending={del.isPending}
        />
      )}
      <InlineStatusAlert keyRow={keyRow} />
    </div>
  );
}

function ByokExplainer({ firstTime }: { firstTime: boolean }) {
  const defaultOpen = firstTime ? "what" : undefined;
  return (
    <Accordion
      type="single"
      collapsible
      defaultValue={defaultOpen}
      className="mb-6 rounded-lg border border-border/50 bg-muted/20 px-4"
    >
      <AccordionItem value="what" className="border-b-border/40">
        <AccordionTrigger className="text-sm text-foreground">
          <span className="flex items-center gap-2">
            <Question className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            O que é BYOK?
          </span>
        </AccordionTrigger>
        <AccordionContent className="text-sm text-muted-foreground">
          <p>
            <strong className="text-foreground">Bring Your Own Key.</strong> Você fornece
            sua chave da OpenAI e o sistema usa para análises,
            geração de copy e transcrição. A chave fica criptografada e nunca aparece em
            logs ou telas.
          </p>
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="which" className="border-b-border/40">
        <AccordionTrigger className="text-sm text-foreground">
          <span className="flex items-center gap-2">
            <Question className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            Qual chave é melhor cadastrar?
          </span>
        </AccordionTrigger>
        <AccordionContent className="text-sm text-muted-foreground space-y-2">
          <p>
            <strong className="text-foreground">Cadastre a OpenAI.</strong> Ela atende
            geração de texto, análise, visão e transcrição. O sistema escolhe entre
            GPT-5.6 Luna, Terra e Sol conforme cada função.
          </p>
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="cost" className="border-b-0">
        <AccordionTrigger className="text-sm text-foreground">
          <span className="flex items-center gap-2">
            <CurrencyDollar className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            Quanto custa?
          </span>
        </AccordionTrigger>
        <AccordionContent className="text-sm text-muted-foreground space-y-2">
          <p>
            Você paga direto para o provedor escolhido, apenas pelo consumo real. Nada é
            cobrado pela plataforma além do seu plano.
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Análise de consulta de 30 min: <strong>≈ R$ 0,30 a R$ 0,80</strong></li>
            <li>Roteiro / mensagem curta: <strong>≈ R$ 0,05</strong></li>
          </ul>
          <p className="text-xs text-muted-foreground/80">
            Valores estimados — variam conforme câmbio e duração do conteúdo.
          </p>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function ProviderCard({
  cfg,
  keyRow,
}: {
  cfg: ProviderConfig;
  keyRow: ByokKey | null;
}) {
  const connected = keyRow != null && isUsable(keyRow);
  return (
    <div
      className={
        "rounded-xl border p-4 transition-colors " +
        (connected
          ? "border-emerald-500/30 bg-emerald-500/[0.04]"
          : cfg.required
            ? "border-rose-500/30 bg-rose-500/[0.04]"
            : "border-border/60 bg-card/40")
      }
    >
      <div className="flex items-start gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <cfg.Icon className="size-5" weight="duotone" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {cfg.required ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 px-2 py-0.5 text-xs font-medium text-rose-300">
                Obrigatória
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/15 px-2 py-0.5 text-xs font-medium text-sky-300">
                Recomendada
              </span>
            )}
            {connected ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-300">
                <CheckCircle weight="fill" className="size-3" /> Conectada
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-200">
                <WarningCircle weight="fill" className="size-3" /> Faltando
              </span>
            )}
          </div>
          <h3 className="mt-0.5 text-base font-semibold text-foreground">
            {cfg.label}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">{cfg.tagline}</p>
        </div>
      </div>

      <div className="mt-4">
        {keyRow ? <KeyRow keyRow={keyRow} /> : <AddKeyForm provider={cfg.id} placeholder={cfg.placeholder} />}
      </div>

      <Accordion type="single" collapsible className="mt-3">
        <AccordionItem value="how" className="border-b-0">
          <AccordionTrigger className="py-2 text-xs text-muted-foreground hover:text-foreground">
            <span className="flex items-center gap-1.5">
              <Question className="size-3.5" aria-hidden="true" />
              Como obter uma chave {cfg.label}?
            </span>
          </AccordionTrigger>
          <AccordionContent className="text-xs text-muted-foreground space-y-2 pt-1">
            <ol className="list-decimal pl-5 space-y-1">
              {cfg.howToSteps.map((s) => <li key={s}>{s}</li>)}
            </ol>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <a
                href={cfg.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                Abrir painel {cfg.label}
                <ArrowSquareOut className="size-3" aria-hidden="true" />
              </a>
              <a
                href={cfg.billingLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                Adicionar saldo (Billing)
                <ArrowSquareOut className="size-3" aria-hidden="true" />
              </a>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

export function ApiKeysSection() {
  useSyncByokToken();
  const query = useByokKeys();
  const list = query.data ?? [];
  const hasZeroKeys = list.length === 0;
  const openaiOk = list.some((k) => k.provider === "openai" && isUsable(k));

  return (
    <div id="api-keys" className="bg-card border border-border/50 rounded-xl p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-blue-500/10">
          <Key className="w-5 h-5 text-blue-400" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Chaves de IA</h2>
          <p className="text-sm text-muted-foreground">
            Conecte sua chave OpenAI. O sistema usa GPT-5.6 Luna, Terra ou Sol conforme a função.
          </p>
        </div>
      </div>

      {query.data && (
        <div
          className={
            "mb-4 rounded-md border px-3 py-2 text-sm " +
            (!openaiOk
              ? "border-rose-500/30 bg-rose-500/[0.06] text-rose-200"
              : "border-emerald-500/20 bg-emerald-500/[0.04] text-emerald-200/90")
          }
        >
          {!openaiOk ? (
            <>
              <WarningCircle weight="fill" className="-mt-0.5 mr-1 inline size-4" />
              OpenAI é obrigatória — sem ela transcrição e geração não funcionam.
            </>
          ) : (
            <>
              <CheckCircle weight="fill" className="-mt-0.5 mr-1 inline size-4" />
              OpenAI conectada. GPT-5.6 Luna, Terra e Sol disponíveis para roteamento.
            </>
          )}
        </div>
      )}

      {query.data && <ByokExplainer firstTime={hasZeroKeys} />}

      {query.isPending && <LoadingState variant="skeleton" rows={2} />}
      {query.isError && (
        <ErrorState
          variant="page"
          message="Não foi possível carregar suas chaves."
          retry={() => void query.refetch()}
        />
      )}

      {query.data && (
        <ol className="flex flex-col gap-3">
          {VISIBLE_PROVIDERS.map((p) => (
            <li key={p.id}>
              <ProviderCard cfg={p} keyRow={list.find((k) => k.provider === p.id) ?? null} />
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
